import { Badge } from "@/components/Badge";
import { StatPill } from "@/components/StatPill";
import { AlertTriangle, TrendingDown, Users } from "lucide-react";

export const StartupFailureHeroSection = () => {
  return (
    <section className="pt-8 sm:pt-12">
      <Badge>
        <AlertTriangle className="h-3 w-3 mr-1" />
        Startup Leadership
      </Badge>
      
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mt-4 mb-3 leading-tight">
        Why Startups Fail: The Founder Delegation Crisis
      </h1>
      
      <p className="text-lg sm:text-xl text-muted-foreground mb-6 max-w-3xl">
        90% of startups fail—and the inability to delegate is a leading cause. Learn why founders struggle to let go, and how to build a team that scales without you.
      </p>
      
      <div className="flex flex-wrap gap-3 mb-8">
        <StatPill value="90%" label="startups fail" />
        <StatPill value="23%" label="fail from wrong team" />
        <StatPill value="70%" label="founders burned out" />
      </div>

      <div className="bg-muted/30 border border-border rounded-xl p-6 sm:p-8">
        <h2 id="overview" className="text-xl sm:text-2xl font-semibold text-foreground mb-4 flex items-center gap-2">
          <TrendingDown className="h-5 w-5 text-destructive" />
          The $1.7 Trillion Delegation Problem
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <p className="text-muted-foreground mb-4">
              According to CB Insights, the top reasons startups fail are directly or indirectly related to founder behavior:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary font-semibold">38%</span> ran out of cash (often due to lack of financial delegation)
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-semibold">35%</span> had no market need (founders too busy to validate)
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-semibold">23%</span> didn't have the right team (failed to hire/delegate)
              </li>
            </ul>
          </div>
          <div>
            <p className="text-muted-foreground mb-4">
              <strong className="text-foreground">Founder Syndrome</strong> is the pattern where founders cannot or will not hand off responsibilities, leading to:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                Team members who feel micromanaged and leave
              </li>
              <li className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-primary" />
                Burnout affecting 72% of entrepreneurs (Harvard study)
              </li>
              <li className="flex items-center gap-2">
                <TrendingDown className="h-4 w-4 text-primary" />
                Growth bottlenecks when everything requires founder approval
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};
